// src/lib/kpi.js
import { loadData, saveData } from "../api/awsClient";

/**
 * KPI Event stored in the workspace doc (settings.kpiEvents):
 * { fp, ts, type, title?, ms?, startIso?, endIso?, meta? }
 *
 * HOW THE KPI PAGE COUNTS
 * Every tile and its drill-down read the same METRICS entry below, so the
 * number on a tile is computed from the very rows its detail panel lists.
 *
 * Outcomes (SUCCESS_TYPES): one event per thing BossDrop actually did. Status
 * pings count once per person per local day ("at most one ping a day").
 *
 * Failures (FAILURE_TYPES): an action that was attempted and did not complete.
 * action_error / action_failed are the executor's catch-all for a thrown
 * error. For create_task, update_task, schedule_meeting, send_ping and
 * send_escalation the executor has already logged that action's own failure
 * event (task_error, task_update_error, meeting_error, ping_failed,
 * escalation_failed), so the catch-all never counts for them: a thrown ping
 * error is one failure, not two. For reply_email, ask_clarifying and
 * send_weekly_report the catch path is gaining its own event
 * (email_reply_failed, clarify_failed, report_failed); until then the
 * catch-all is the only record, so it counts unless it pairs with that event
 * from the same throw (same action type, within PAIR_WINDOW_MS, same err when
 * both carry one). Any other action type's catch-all counts. Ping failures
 * count once per person per local day, the same unit as a sent ping (the
 * executor retries a failed ping each tick).
 *
 * Setup warnings (ping_warning, clarify_warning: a teammate BossDrop could
 * not resolve, a missing bot permission) are not failed actions and never
 * touch Reliability (Eric, Oct 19: "reliability should only be affected on
 * failed actions"). They stay in the audit log.
 *
 * Reliability = executed / (executed + failed). null with no attempts.
 *
 * Automations executed = max(per-action outcome events, sum of the brain's
 * executedCount). Every action the brain executes also logs its own outcome
 * event (report_sent and email_reply_sent included), so the two measure the
 * same actions: the max covers a best-effort KPI write lost on either side
 * without adding them together.
 *
 * Decision Rate = executed / (executed + held back), from the brain summaries.
 * Held back = brain_execute_summary skippedCount plus the brain_dedup skips.
 * Failures are a Reliability problem, not a decision: a summary that carries
 * failedCount keeps its failures out of skippedCount (the executor's separate
 * failed[]), and failedCount is never added to skips or to the rate. Older
 * summaries without failedCount filed failures under skipped and cannot be
 * split after the fact. A window whose brain summaries record no decisions
 * falls back to the per-action outcome and skip events. null when nothing was
 * decided.
 *
 * People impacted: recipients of outcome events only (never of a failed,
 * warned or skipped send).
 *
 * Minutes saved: MINUTES_SAVED per counted outcome (an explicit
 * meta.minutesSaved wins). Email replies are 3 min each: a short, already
 * drafted answer. A duplicate meeting prevented is 10 min, counted once per
 * meeting title per local day, and not at all when the skip could not be
 * verified (reason duplicate_unverified: the meeting may not exist).
 *
 * Windows are local calendar days: [start 00:00, the day after end 00:00).
 * Date math is done on calendar days, never on 24 h multiples, so DST weeks
 * keep their length.
 */

let ACTIVE_EMAIL = "";

/** Call once when you know the user (Auth.me). */
export function kpiConfigure({ email }) {
  ACTIVE_EMAIL = String(email || "").toLowerCase();
}

/* ----------------- S3 helpers ----------------- */
async function withSettings(email, mergeFn) {
  const doc = (await loadData(email)) || {};
  const s = doc.settings || doc;
  const next = await mergeFn(s);
  const payload = {
    version: doc.version ?? 1,
    updatedAt: new Date().toISOString(),
    settings: next,
    tasks: doc.tasks ?? {},
  };
  await saveData(email, payload);
  return next;
}

/* ----------------- Date helpers (LOCAL calendar days) ----------------- */
/** Local YYYY-MM-DD (matches how users think about dates). */
export function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYMD(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  return m ? { y: Number(m[1]), mo: Number(m[2]) - 1, d: Number(m[3]) } : null;
}

/** First instant of the local calendar day `ymd` + `plusDays`, in ms. */
function startOfLocalDay(ymd, plusDays = 0) {
  const p = parseYMD(ymd);
  return p ? new Date(p.y, p.mo, p.d + plusDays).getTime() : NaN;
}

/** Calendar-day arithmetic on a local YYYY-MM-DD (DST-safe). */
export function addDays(ymd, n) {
  const p = parseYMD(ymd);
  return p ? toYMD(new Date(p.y, p.mo, p.d + n, 12)) : ymd;
}

function dayNumber(ymd) {
  const p = parseYMD(ymd);
  return p ? Date.UTC(p.y, p.mo, p.d) / 86400000 : NaN;
}

/** Previous window of the same number of local days, ending the day before `start`. */
export function previousWindow(start, end) {
  const span = dayNumber(end) - dayNumber(start) + 1;
  const days = Number.isFinite(span) ? Math.max(1, span) : 1;
  return { start: addDays(start, -days), end: addDays(start, -1) };
}

/** The default KPI window: the last `days` local calendar days, ending today. */
export function defaultRange(now = new Date(), days = 28) {
  const end = toYMD(now);
  return { start: addDays(end, -(days - 1)), end };
}

/* ----------------- Fingerprints (CRITICAL for correct counts) ----------------- */
function pickEmailLike(...vals) {
  for (const v of vals) {
    const s = typeof v === "string" ? v.trim().toLowerCase() : "";
    if (s && s.includes("@")) return s;
  }
  return "";
}
function localDayFromIso(iso) {
  try {
    return iso ? toYMD(new Date(iso)) : "";
  } catch {
    return "";
  }
}

function getMeta(e) {
  return e?.meta && typeof e.meta === "object" ? e.meta : {};
}

/** The person a ping-like event was addressed to. */
function recipientOf(e) {
  const m = getMeta(e);
  return pickEmailLike(
    m.toEmail,
    m.to,
    m.recipientEmail,
    m.recipient,
    m.email,
    m.ownerEmail,
    m.owner,
    m.assignee,
  );
}

function titleOf(e) {
  const m = getMeta(e);
  return String(e?.title || m.title || m.topic || m.about || "").trim();
}

/** Stable fingerprint per event (prevents duplicate counts across merges/devices). */
function fpOf(e) {
  const t = (x) => (x == null ? "" : String(x).trim());
  const type = t(e?.type);
  const meta = getMeta(e);
  const ts = t(e?.ts);
  const dayKey = localDayFromIso(ts);

  // If already provided, respect it
  if (t(e?.fp)) return t(e.fp);
  if (t(meta?.fp)) return t(meta.fp);

  const toEmail = recipientOf(e);

  switch (type) {
    case "meeting_scheduled":
      return `ms|${t(e.title)}|${t(e.startIso)}`;

    case "meeting_rescheduled":
      return `mr|${t(e.title)}|${t(e.from)}>${t(e.to)}`;

    case "report_duration": {
      // bucket by minute so quick successive logs don't explode
      const m = new Date(e.ts || Date.now());
      m.setSeconds(0, 0);
      return `rd|${m.toISOString()}|${Math.round(Number(e.ms) || 0)}`;
    }

    case "report_sent": {
      const recipients = t(meta.recipients || meta.to || "");
      return `rs|${dayKey}|${recipients.toLowerCase()}`;
    }

    // Product intent is "at most 1 ping/day/person" → dedupe by day+recipient.
    case "ping_sent":
      return `ps|${dayKey}|${toEmail}`;

    case "ping_failed":
      return `pf|${dayKey}|${toEmail}`;

    case "clarify_sent":
      return `cs|${dayKey}|${toEmail}|${t(meta.title || e.title || "")}`;

    case "escalation_sent":
      return `es|${dayKey}|${toEmail}|${t(e.title || meta.about || "")}`;

    case "escalation_failed":
      return `ef|${dayKey}|${toEmail}|${t(e.title || meta.about || "")}`;

    case "escalation_skipped":
      return `esk|${dayKey}|${toEmail}|${t(meta.reason || "")}`;

    case "task_created":
      return `tc|${dayKey}|${t(meta.taskId || meta.id || "")}|${t(e.title || meta.title || "")}|${toEmail}`;

    case "task_updated":
      return `tu|${dayKey}|${t(meta.taskId || meta.id || e.title || "")}`;

    case "task_dedup_skipped":
      return `tds|${dayKey}|${t(e.title || meta.title || "")}|${toEmail}`;

    case "task_deferred_ai":
      return `tdef|${dayKey}|${t(e.title || meta.title || "")}|${toEmail}`;

    case "meeting_dedup_skipped":
      return `mds|${dayKey}|${titleOf(e)}`;

    case "meeting_skipped":
      return `msk|${dayKey}|${titleOf(e)}|${t(meta.reason || "")}`;

    case "meeting_error":
      return `me|${dayKey}|${titleOf(e)}|${t(meta.reason || "")}`;

    // Brain summaries: one per run, keyed by the run's timestamp.
    case "brain_plan":
      return `bp|${t(ts)}|${t(meta.actions)}`;

    case "brain_dedup":
      return `bd|${t(ts)}|${t(meta.proposed)}|${t(meta.kept)}`;

    case "brain_execute_summary":
      return `be|${t(ts)}|${t(meta.executedCount)}|${t(meta.skippedCount)}|${t(meta.failedCount)}`;

    default:
      // Stable fallback
      return `x|${type}|${dayKey}|${t(e.title || "")}|${t(ts)}`;
  }
}

function fpForDedup(raw) {
  if (!raw || typeof raw !== "object") return "";
  const type = String(raw.type || "");
  const isBrainLike =
    type === "brain_plan" ||
    type === "brain_dedup" ||
    type === "brain_execute_summary";

  if (isBrainLike) {
    // Ignore legacy fp fields for brain summaries to avoid collapsing
    // multiple runs that share the same day-level counts.
    const clone = { ...raw, fp: null };
    if (clone.meta && typeof clone.meta === "object") {
      clone.meta = { ...clone.meta, fp: null };
    }
    return fpOf(clone);
  }

  return raw.fp || fpOf(raw);
}

/** Append events to S3 (dedup by fp, cap to last N). */
async function appendEvents(email, list, cap = 5000) {
  if (!email || !list?.length) return;
  await withSettings(email, (cur) => {
    const events = Array.isArray(cur.kpiEvents) ? cur.kpiEvents.slice() : [];
    const seen = new Set(events.map((e) => fpForDedup(e)).filter(Boolean));

    const toAdd = [];
    for (const raw of list) {
      const e = { ...raw };
      e.ts = e.ts || new Date().toISOString();
      e.fp = fpForDedup(e);
      if (seen.has(e.fp)) continue;
      seen.add(e.fp);
      toAdd.push(e);
    }

    const next = [...events, ...toAdd];
    if (next.length > cap) next.splice(0, next.length - cap);

    return {
      ...cur,
      kpiEvents: next,
      kpiLastSyncedAt: new Date().toISOString(),
    };
  });
}

/* ----------------- Public loggers ----------------- */
export async function kpiLogReportDuration(ms, meta = {}) {
  if (!ACTIVE_EMAIL) return;
  const e = {
    type: "report_duration",
    ms: Math.max(0, Math.round(ms)),
    meta,
    ts: new Date().toISOString(),
  };
  e.fp = fpOf(e);
  await appendEvents(ACTIVE_EMAIL, [e]);
}

export async function kpiLogEventScheduled({
  title,
  startIso,
  endIso,
  eventId,
  organizerEmail,
  attendees,
}) {
  if (!ACTIVE_EMAIL || !title || !startIso) return;
  const e = {
    type: "meeting_scheduled",
    title: String(title),
    startIso,
    endIso: endIso || null,
    meta: {
      eventId: eventId || null,
      organizerEmail: organizerEmail || null,
      attendees: attendees || [],
    },
    ts: new Date().toISOString(),
  };
  e.fp = fpOf(e);
  await appendEvents(ACTIVE_EMAIL, [e]);
}

/* --------------- Load --------------- */
export async function getAllEvents(email) {
  const doc = (await loadData(email)) || {};
  const s = doc.settings || doc || {};

  // support a few possible historical locations
  const a = Array.isArray(s.kpiEvents) ? s.kpiEvents : [];
  const b = Array.isArray(doc.kpiEvents) ? doc.kpiEvents : [];
  const c = Array.isArray(doc.tasks?.kpiEvents) ? doc.tasks.kpiEvents : [];

  return dedupeEvents([...a, ...b, ...c]);
}

/** Events with a timestamp, one per fingerprint, in their original order. */
function dedupeEvents(list) {
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(list) ? list : []) {
    if (!raw || typeof raw !== "object" || !raw.ts) continue;
    const e = { ...raw };
    e.fp = fpForDedup(e);
    if (seen.has(e.fp)) continue;
    seen.add(e.fp);
    out.push(e);
  }
  return out;
}

function tsOf(e) {
  const t = new Date(e?.ts).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/* --------------- Counting helpers --------------- */
function toCount(v) {
  if (typeof v === "number" && Number.isFinite(v)) {
    return Math.max(0, Math.round(v));
  }
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return Math.max(0, Math.round(n));
  }
  return null;
}

function readCount(e, keys = [], arrayKeys = []) {
  const m = getMeta(e);

  for (const k of keys) {
    const nMeta = toCount(m?.[k]);
    if (nMeta != null) return nMeta;
    const nTop = toCount(e?.[k]);
    if (nTop != null) return nTop;
  }

  for (const k of arrayKeys) {
    if (Array.isArray(m?.[k])) return m[k].length;
    if (Array.isArray(e?.[k])) return e[k].length;
  }

  return null;
}

/** brain_dedup: actions the dedup stage dropped (proposed minus kept). */
function brainDedupSkipped(e) {
  const proposed = readCount(
    e,
    ["proposed", "proposedCount", "totalProposed", "actions"],
    ["proposed", "proposedActions", "actions"],
  );
  const kept = readCount(e, ["kept", "keptCount"], ["kept", "keptActions"]);
  const skipped = readCount(
    e,
    ["skippedCount", "skipped", "dedupSkipped"],
    ["skipped", "skippedItems", "skippedPreview"],
  );
  if (skipped != null) return skipped;
  if (proposed != null && kept != null) return Math.max(0, proposed - kept);
  return 0;
}

/**
 * brain_execute_summary counts. failedCount (when present) is reported apart
 * from skippedCount, so "held back" is skippedCount alone: failures never
 * count as decisions.
 */
function brainExecuteCounts(e) {
  const executed =
    readCount(
      e,
      ["executedCount", "executed", "actionsExecuted"],
      ["executed", "executedActions"],
    ) || 0;
  const heldBack =
    readCount(
      e,
      ["skippedCount", "skipped", "skips"],
      ["skipped", "skippedItems"],
    ) || 0;
  return { executed, heldBack };
}

function pushPeople(set, v) {
  if (Array.isArray(v)) {
    for (const x of v) pushPeople(set, x);
    return;
  }
  if (v && typeof v === "object") {
    pushPeople(set, v.email || v.address || v.userPrincipalName);
    return;
  }
  if (typeof v !== "string") return;
  for (const part of v.split(/[,;]+/g)) {
    const s = part.trim().toLowerCase();
    if (s.includes("@") && s.length <= 160) set.add(s);
  }
}

/** Who an OUTCOME event reached. Failed, warned and skipped events reach nobody. */
function peopleOf(e) {
  const m = getMeta(e);
  const set = new Set();
  switch (e?.type) {
    case "ping_sent":
    case "clarify_sent":
    case "escalation_sent":
    case "email_reply_sent":
      pushPeople(set, m.to);
      pushPeople(set, m.toEmail);
      pushPeople(set, m.recipient);
      break;
    case "report_sent":
      pushPeople(set, m.to);
      pushPeople(set, m.recipients);
      break;
    case "task_created":
    case "task_updated":
      pushPeople(set, m.assignee);
      pushPeople(set, m.ownerEmail);
      pushPeople(set, m.owner);
      break;
    case "meeting_scheduled":
      pushPeople(set, m.attendees);
      break;
    default:
      break;
  }
  return [...set];
}

/* --------------- Metric definitions (shared by tiles and drill-downs) --------------- */
export const SUCCESS_TYPES = Object.freeze([
  "report_sent",
  "ping_sent",
  "clarify_sent",
  "escalation_sent",
  "task_created",
  "task_updated",
  "meeting_scheduled",
  "email_reply_sent",
]);

export const FAILURE_TYPES = Object.freeze([
  "ping_failed",
  "clarify_failed",
  "escalation_failed",
  "meeting_error",
  "task_error",
  "task_update_error",
  "email_reply_failed",
  "report_failed",
  "action_failed",
  "action_error",
]);

/** Action types whose thrown error already logs its own failure event. */
export const ACTIONS_WITH_OWN_FAILURE_EVENT = Object.freeze([
  "create_task",
  "update_task",
  "schedule_meeting",
  "send_ping",
  "send_escalation",
]);

export const SKIP_TYPES = Object.freeze([
  "task_dedup_skipped",
  "task_deferred_ai",
  "meeting_dedup_skipped",
  "meeting_skipped",
  "escalation_skipped",
]);

/** Per-outcome time assumptions, in minutes (see the header). */
export const MINUTES_SAVED = Object.freeze({
  report_sent: 30,
  escalation_sent: 6,
  task_created: 3,
  meeting_scheduled: 3,
  email_reply_sent: 3,
  ping_sent: 2,
  clarify_sent: 2,
  task_updated: 2,
  meeting_dedup_skipped: 10,
});

/**
 * Action types whose catch path is gaining its own failure event, logged next
 * to the catch-all; the catch-all counts only when it has no such partner.
 */
export const CATCH_FAILURE_EVENT = Object.freeze({
  reply_email: "email_reply_failed",
  ask_clarifying: "clarify_failed",
  send_weekly_report: "report_failed",
});
const PAIR_WINDOW_MS = 60000;

const BRAIN_EXEC = "brain_execute_summary";
const BRAIN_DEDUP = "brain_dedup";
const GENERIC_FAILURE = new Set(["action_error", "action_failed"]);
const OWN_FAILURE_EVENT = new Set(ACTIONS_WITH_OWN_FAILURE_EVENT);
const FAILURE = new Set(FAILURE_TYPES);

/**
 * Catch-all failures that pair one-to-one with their action's own failure
 * event from the same throw (see CATCH_FAILURE_EVENT), nearest first.
 */
function pairedCatchAlls(all) {
  const wanted = new Set(Object.values(CATCH_FAILURE_EVENT));
  const own = all.filter((e) => wanted.has(e.type));
  const used = new Set();
  const paired = new Set();
  if (!own.length) return paired;
  for (const g of all) {
    if (!GENERIC_FAILURE.has(g.type)) continue;
    const want = CATCH_FAILURE_EVENT[String(getMeta(g).type || "")];
    if (!want) continue;
    const gt = tsOf(g);
    const gErr = getMeta(g).err;
    let best = null;
    for (const f of own) {
      if (used.has(f) || f.type !== want) continue;
      const fErr = getMeta(f).err;
      if (gErr != null && fErr != null && String(gErr) !== String(fErr)) continue;
      const d = Math.abs(tsOf(f) - gt);
      if (!(d <= PAIR_WINDOW_MS)) continue;
      if (!best || d < best.d) best = { f, d };
    }
    if (best) {
      used.add(best.f);
      paired.add(g);
    }
  }
  return paired;
}

/** True when a failure event counts (see the header on catch-alls). */
function isCountedFailure(e, ctx) {
  if (!FAILURE.has(e?.type)) return false;
  if (!GENERIC_FAILURE.has(e.type)) return true;
  if (OWN_FAILURE_EVENT.has(String(getMeta(e).type || ""))) return false;
  if (!ctx.paired) ctx.paired = pairedCatchAlls(ctx.all);
  return !ctx.paired.has(e);
}

const pingDayKey = (e) => `${localDayFromIso(e.ts)}|${recipientOf(e)}`;
const isPingLike = (e) => e.type === "ping_sent" || e.type === "ping_failed";
/** Pings (sent or failed) count once per person per local day. */
const pingDedupeKey = (e) =>
  isPingLike(e) ? `${e.type}|${pingDayKey(e)}` : null;
const executedSummary = (e) =>
  e.type !== BRAIN_EXEC || brainExecuteCounts(e).executed > 0;

/**
 * One entry per tile. `types` is what the tile counts (and what its drill-down
 * lists); `include` and `dedupeKey` narrow it the same way for both. `select`
 * replaces the default filter when a metric needs events outside the window.
 */
const METRICS = {
  reports: { types: ["report_sent"] },
  pings: { types: ["ping_sent"], dedupeKey: pingDedupeKey },
  clarifications: { types: ["clarify_sent"] },
  escalations: { types: ["escalation_sent"] },
  tasksCreated: { types: ["task_created"] },
  tasksUpdated: { types: ["task_updated"] },
  meetings: { types: ["meeting_scheduled"] },
  replies: { types: ["email_reply_sent"] },
  duplicateMeetingsPrevented: {
    types: ["meeting_dedup_skipped"],
    include: (e) => getMeta(e).reason !== "duplicate_unverified",
    dedupeKey: (e) =>
      `${localDayFromIso(e.ts)}|${titleOf(e).toLowerCase()}`,
  },
  reschedules: {
    types: ["meeting_rescheduled", "meeting_scheduled"],
    select: selectReschedules,
  },
  reportTime: {
    types: ["report_duration"],
    include: (e) => Number.isFinite(Number(e.ms)),
  },
  executed: {
    types: [...SUCCESS_TYPES, BRAIN_EXEC],
    include: executedSummary,
    dedupeKey: pingDedupeKey,
  },
  skipped: {
    types: [...SKIP_TYPES, BRAIN_EXEC, BRAIN_DEDUP],
    include: (e) =>
      e.type === BRAIN_EXEC
        ? brainExecuteCounts(e).heldBack > 0
        : e.type === BRAIN_DEDUP
          ? brainDedupSkipped(e) > 0
          : true,
  },
  reliability: {
    types: [...FAILURE_TYPES],
    include: (e, ctx) => isCountedFailure(e, ctx),
    dedupeKey: pingDedupeKey,
  },
  decisionRate: {
    types: [BRAIN_EXEC, BRAIN_DEDUP],
    include: (e) => {
      if (e.type === BRAIN_DEDUP) return brainDedupSkipped(e) > 0;
      const c = brainExecuteCounts(e);
      return c.executed + c.heldBack > 0;
    },
  },
  activeDays: {
    types: [...SUCCESS_TYPES, BRAIN_EXEC],
    include: executedSummary,
  },
  peopleImpacted: {
    types: [...SUCCESS_TYPES],
    include: (e) => peopleOf(e).length > 0,
  },
};

/** Metric key -> the event types it counts (the drill-down lists the same). */
export const METRIC_TYPES = Object.freeze(
  Object.fromEntries(
    Object.entries(METRICS).map(([k, def]) => [k, Object.freeze([...def.types])]),
  ),
);

/**
 * Reschedules: explicit meeting_rescheduled events, plus a meeting_scheduled
 * whose start differs from the previous booking with the same title (the
 * earlier booking may predate the window).
 */
function selectReschedules({ inRange, all, from, to }) {
  const out = inRange.filter((e) => e.type === "meeting_rescheduled");
  const byTitle = new Map();
  for (const e of all) {
    if (e.type !== "meeting_scheduled" || !e.title || !e.startIso) continue;
    const arr = byTitle.get(e.title) || [];
    arr.push(e);
    byTitle.set(e.title, arr);
  }
  for (const [, arr] of byTitle) {
    arr.sort((a, b) => tsOf(a) - tsOf(b));
    for (let i = 1; i < arr.length; i++) {
      const t = tsOf(arr[i]);
      if (arr[i].startIso !== arr[i - 1].startIso && t >= from && t < to) {
        out.push(arr[i]);
      }
    }
  }
  return out;
}

function selectRows(key, ctx) {
  const def = METRICS[key];
  if (!def) return [];
  if (def.select) return def.select(ctx);
  const wanted = new Set(def.types);
  const seen = new Set();
  const out = [];
  for (const e of ctx.inRange) {
    if (!wanted.has(e.type)) continue;
    if (def.include && !def.include(e, ctx)) continue;
    const k = def.dedupeKey ? def.dedupeKey(e) : null;
    if (k != null) {
      if (seen.has(k)) continue;
      seen.add(k);
    }
    out.push(e);
  }
  return out;
}

/** Deduped events in [startYmd, endYmd] (local days), oldest first, plus context. */
function windowContext(events, startYmd, endYmd) {
  const all = dedupeEvents(events);
  const from = startOfLocalDay(startYmd);
  const to = startOfLocalDay(endYmd, 1);
  const inRange = all
    .filter((e) => {
      const t = tsOf(e);
      return t >= from && t < to;
    })
    .sort((a, b) => tsOf(a) - tsOf(b));
  return { all, inRange, from, to };
}

/** The events behind one metric in [startYmd, endYmd], newest first (drill-downs). */
export function metricEvents(events, key, startYmd, endYmd) {
  const rows = selectRows(key, windowContext(events, startYmd, endYmd));
  return rows.slice().sort((a, b) => tsOf(b) - tsOf(a));
}

function minutesFor(e) {
  const m = getMeta(e);
  for (const v of [m.minutesSaved, m.minutes_saved, m.savedMinutes]) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return MINUTES_SAVED[e.type] || 0;
}

const MINUTE_METRICS = [
  "reports",
  "pings",
  "clarifications",
  "escalations",
  "tasksCreated",
  "tasksUpdated",
  "meetings",
  "replies",
  "duplicateMeetingsPrevented",
];

/** Compute KPI summary from events in [start,end] (YYYY-MM-DD inclusive, LOCAL). */
export function summarize(events, startYmd, endYmd) {
  const ctx = windowContext(events, startYmd, endYmd);
  const rows = (key) => selectRows(key, ctx);
  const count = (key) => rows(key).length;

  /* ---------- Outcomes ---------- */
  const reports = count("reports");
  const pingsSent = count("pings");
  const clarifysSent = count("clarifications");
  const escalationsSent = count("escalations");
  const tasksCreated = count("tasksCreated");
  const tasksUpdated = count("tasksUpdated");
  const meetings = count("meetings");
  const repliesSent = count("replies");
  const duplicateMeetingsPrevented = count("duplicateMeetingsPrevented");

  // Lower-is-better metrics are null when the window has nothing to measure,
  // so an empty window never reads as an improvement.
  const rescheduleCount = count("reschedules");
  const reschedules =
    rescheduleCount > 0 || meetings > 0 ? rescheduleCount : null;

  const durs = rows("reportTime");
  const avgReportMinutes = durs.length
    ? durs.reduce((s, e) => s + Number(e.ms), 0) / durs.length / 60000
    : null;

  /* ---------- Executed ---------- */
  let outcomeExecuted = 0;
  let brainExecuted = 0;
  for (const e of rows("executed")) {
    if (e.type === BRAIN_EXEC) brainExecuted += brainExecuteCounts(e).executed;
    else outcomeExecuted += 1;
  }
  const automationsExecuted = Math.max(outcomeExecuted, brainExecuted);

  /* ---------- Skipped / held back ---------- */
  let skipEvents = 0;
  let brainHeldBack = 0;
  for (const e of rows("skipped")) {
    if (e.type === BRAIN_EXEC) brainHeldBack += brainExecuteCounts(e).heldBack;
    else if (e.type === BRAIN_DEDUP) brainHeldBack += brainDedupSkipped(e);
    else skipEvents += 1;
  }
  const skippedTotal = Math.max(skipEvents, brainHeldBack);

  /* ---------- Decision Rate ---------- */
  const decisionRows = rows("decisionRate");
  let decisionExecuted = 0;
  let decisionHeldBack = 0;
  if (decisionRows.length) {
    for (const e of decisionRows) {
      if (e.type === BRAIN_DEDUP) {
        decisionHeldBack += brainDedupSkipped(e);
      } else {
        const c = brainExecuteCounts(e);
        decisionExecuted += c.executed;
        decisionHeldBack += c.heldBack;
      }
    }
  } else {
    decisionExecuted = outcomeExecuted;
    decisionHeldBack = skipEvents;
  }
  const decisionTotal = decisionExecuted + decisionHeldBack;
  const executionRate = decisionTotal ? decisionExecuted / decisionTotal : null;

  /* ---------- Reliability ---------- */
  const failures = count("reliability");
  const attempts = automationsExecuted + failures;
  const successRate = attempts ? automationsExecuted / attempts : null;

  const automationsSkipped =
    attempts > 0 || skippedTotal > 0 ? skippedTotal : null;

  /* ---------- Active days ---------- */
  const days = new Set(rows("activeDays").map((e) => localDayFromIso(e.ts)));
  days.delete("");
  const activeDays = days.size;

  /* ---------- People impacted ---------- */
  const people = new Set();
  for (const e of rows("peopleImpacted")) {
    for (const p of peopleOf(e)) people.add(p);
  }
  const peopleImpacted = people.size;

  /* ---------- Minutes saved ---------- */
  let minutesSaved = 0;
  for (const key of MINUTE_METRICS) {
    for (const e of rows(key)) minutesSaved += minutesFor(e);
  }

  return {
    reports,
    pingsSent,
    clarifysSent,
    escalationsSent,
    tasksCreated,
    tasksUpdated,
    meetings,
    repliesSent,
    duplicateMeetingsPrevented,
    reschedules,
    avgReportMinutes,

    automationsExecuted,
    automationsSkipped,

    executionRate,
    decisionExecuted,
    decisionHeldBack,

    successRate,
    attempts,
    failures,

    activeDays,
    peopleImpacted,
    minutesSaved,
    eventsStored: ctx.inRange.length,
  };
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Reliability sub-line: "1 failed of 1 attempt", "0 failed of 10 attempts". */
export function describeReliability(s) {
  const attempts = Number(s?.attempts) || 0;
  if (!attempts) return "No attempts in this window";
  return `${Number(s?.failures) || 0} failed of ${plural(attempts, "attempt")}`;
}
